<template>
    <li v-if="visible && !separator" v-bind="$attrs">
        <router-link
            v-if="tag === 'router-link' && to"
            :to="to"
            :class="[linkClasses, { 'is-active': active }]"
            active-class=""
            @click="handleClick"
        >
            <div class="tw:flex tw:items-center tw:gap-3 tw:w-full">
                <span v-if="icon" class="tw:flex tw:items-center tw:shrink-0">
                    <e-icon :icon="icon" size="is-small" />
                </span>
                <span v-if="hasLabelSlot || label">
                    <slot name="label">{{ label }}</slot>
                </span>
                <slot v-if="!hasLabelSlot && !label" />
            </div>
        </router-link>

        <Button
            v-else
            :class="[linkClasses, { 'is-active': active }]"
            unstyled
            @click="handleClick"
        >
            <div class="tw:flex tw:items-center tw:gap-3 tw:w-full">
                <span v-if="icon" class="tw:flex tw:items-center tw:shrink-0">
                    <e-icon :icon="icon" size="is-small" />
                </span>
                <span v-if="hasLabelSlot || label">
                    <slot name="label">{{ label }}</slot>
                </span>
                <slot v-if="!hasLabelSlot && !label" />
            </div>
        </Button>

        <ul v-if="hasNestedItems" class="menu-list tw:ml-3 tw:pl-3 tw:mt-1 tw:border-l tw:border-gray-300">
            <slot />
        </ul>
    </li>

    <li v-else-if="separator && visible" class="tw:my-2">
        <hr class="tw:border-t tw:border-gray-300" />
    </li>
</template>

<script>
import EIcon from './EIcon.vue';

/**
 * EMenuItem - Abstraction wrapper to replicate Buefy b-menuitem functionality
 *
 * Usage:
 *   <e-menu>
 *     <e-menu-item label="Dashboard" icon="dashboard" tag="router-link" to="/dashboard" />
 *     <e-menu-item label="Settings" icon="settings" @click="openSettings" />
 *   </e-menu>
 *
 *   <e-menu>
 *     <e-menu-item label="Account" icon="user">
 *       <e-menu-item label="Profile" @click="goToProfile" />
 *       <e-menu-item label="Logout" @click="logout" />
 *     </e-menu-item>
 *   </e-menu>
 *
 * Props:
 *   - label: String - Menu item label text
 *   - icon: String - Icon name
 *   - tag: String - HTML tag or component to use (router-link, a, etc.)
 *   - to: String/Object - Route destination (for router-link)
 *   - url: String - URL for external links
 *   - target: String - Link target (_blank, etc.)
 *   - active: Boolean - Highlight as active/selected
 *   - disabled: Boolean - Disable the menu item
 *   - separator: Boolean - Render as separator
 *   - visible: Boolean - Show/hide the menu item
 *   - expanded: Boolean - Expand nested items by default
 *
 * Events:
 *   - @click: Emitted when item is clicked
 *   - All other DOM events are supported
 *
 * Slots:
 *   - default: Menu item content (overrides label)
 *   - label: Custom label content
 *
 * All Buefy b-menu-item props, events, and slots are supported in Buefy mode.
 */
export default {
    name: 'EMenuItem',
    components: {
        EIcon,
    },
    inheritAttrs: false,
    props: {
        label: {
            type: String,
            default: undefined,
        },
        icon: {
            type: String,
            default: undefined,
        },
        tag: {
            type: String,
            default: undefined,
        },
        to: {
            type: [String, Object],
            default: undefined,
        },
        url: {
            type: String,
            default: undefined,
        },
        target: {
            type: String,
            default: undefined,
        },
        active: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        separator: {
            type: Boolean,
            default: false,
        },
        visible: {
            type: Boolean,
            default: true,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        linkClasses() {
            return [
                'tw:block',
                'tw:py-2',
                'tw:px-3',
                'tw:rounded',
                'tw:text-gray-700',
                'tw:transition-colors',
                'tw:duration-150',
                {
                    'hover:tw:bg-gray-100 hover:tw:text-gray-900': !this.disabled,
                    'tw:opacity-50 tw:cursor-not-allowed': this.disabled,
                    'tw:cursor-pointer': !this.disabled,
                },
            ];
        },
        hasLabelSlot() {
            return !!this.$slots.label;
        },
        hasNestedItems() {
            // Check if there are nested EMenuItem components in default slot
            return !!this.$slots.default;
        },
    },
    methods: {
        handleClick(event) {
            if (this.disabled) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        },
    },
};
</script>

<style scoped>
.menu-list .menu-list {
    /* Nested list styling */
    padding-left: 0.75em;
    margin: 0.75em 0 0.75em 0.75em;
}
</style>
